import React, { useState, useEffect } from 'react';
import { auth } from '../shared/api/firebase/config';
import { QuestionLoader } from '../utils/QuestionLoader';
import { fetchUserBookmarks, deleteUserBookmark } from '../services/NotebookRepository';
import { ACADEMIA_CONSTANTS } from '../features/academy/AcademiaConstants';

import { FIREBASE_CONFIG } from '../config/Config';

const appId = FIREBASE_CONFIG.projectId;

const NotebookView = ({ user }) =>
 {
    const [savedQuestions, setSavedQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        loadNotebook();
    }, []);

    const loadNotebook = async () => {
        if (!user) return setLoading(false);

        try {
            // 1. Busca os IDs salvos no Firestore
            const snap = await fetchUserBookmarks(user.uid);

            if (snap.empty) {
                setSavedQuestions([]);
                setLoading(false);
                return;
            }

            const bookmarkIds = snap.map(d => d.id);
            const bookmarkDates = snap.reduce((acc, d) => ({...acc, [d.id]: d.savedAt}), {});

            // 2. Carrega o Banco de Questões Completo (Cacheado)
            const allQuestions = await QuestionLoader.loadDatabase();

            // 3. Filtra e Hidrata (Cruzamento de Dados)
            const hydrated = allQuestions
                .filter(q => bookmarkIds.includes(q.id))
                .map(q => ({
                    ...q,
                    savedAt: bookmarkDates[q.id]
                }));

            setSavedQuestions(hydrated);
        } catch (e) {
            console.error("Erro ao carregar caderno:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (qId) => {
        if (!confirm("Remover esta questão do caderno?")) return;
        try {
            await deleteUserBookmark(qId, user.uid);
            setSavedQuestions(prev => prev.filter(q => q.id !== qId));
        } catch (e) {
            alert("Erro ao remover: " + e.message);
        }
    };

    if (loading) return <div className="text-center py-20"><i className="fas fa-circle-notch fa-spin text-3xl text-[#C5A059]"></i><p className="text-xs text-gray-500 mt-2 font-bold uppercase">Carregando anotações...</p></div>;

    if (savedQuestions.length === 0) return (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300 text-3xl">
                <i className="far fa-bookmark"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-700">Seu caderno está vazio</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">
                Durante os simulados, clique no ícone <i className="far fa-bookmark mx-1"></i> para salvar questões importantes para revisão.
            </p>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto pb-20 animate-fade-in">
            <header className="mb-8 flex justify-between items-end border-b border-gray-200 pb-6">
                <div>
                    <h2 className="text-3xl font-bold text-[#20243B] flex items-center gap-3">
                        <i className="fas fa-bookmark text-[#C5A059]"></i> Meu Caderno
                    </h2>
                    <p className="text-sm text-gray-500 mt-2">Revisão espaçada e anotações estratégicas.</p>
                </div>
                <span className="text-xs font-bold bg-[#C5A059]/10 text-[#C5A059] px-3 py-1 rounded-full border border-[#C5A059]/20">
                    {savedQuestions.length} Questões Salvas
                </span>
            </header>

            <div className="space-y-4">
                {savedQuestions.map((q) => {
                    const subject = ACADEMIA_CONSTANTS.SUBJECTS[q.materia] || { label: q.materia, color: 'gray' };
                    const isExpanded = expandedId === q.id;

                    return (
                        <div key={q.id} className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden ${isExpanded ? 'border-[#C5A059] shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}>
                            
                            {/* Header do Card */}
                            <div 
                                onClick={() => setExpandedId(isExpanded ? null : q.id)}
                                className="p-4 flex items-start gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0 bg-${subject.color}-50 text-${subject.color}-600`}>
                                    <i className={`fas ${subject.icon || 'fa-book'}`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase border bg-${subject.color}-50 text-${subject.color}-700 border-${subject.color}-100`}>
                                            {subject.label}
                                        </span>
                                        <span className="text-[9px] font-mono text-gray-400 border border-gray-100 px-1.5 rounded bg-gray-50">
                                            {q.banca} / {q.ano}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-gray-800 line-clamp-2 leading-relaxed">
                                        {q.enunciado}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-2 items-center">
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleRemove(q.id); }}
                                        className="text-gray-300 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-all"
                                        title="Remover do Caderno"
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                    <i className={`fas fa-chevron-down text-gray-300 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}></i>
                                </div>
                            </div>

                            {/* Conteúdo Expandido (Gabarito e Comentário) */}
                            {isExpanded && (
                                <div className="p-6 border-t border-gray-100 bg-gray-50/50 animate-fade-in-down">
                                    <div className="space-y-2 mb-6 pl-4 border-l-2 border-gray-200">
                                        {q.alternativas.map((alt, idx) => (
                                            <div key={idx} className={`text-xs p-2 rounded ${idx === q.correta ? 'bg-green-100 text-green-800 font-bold border border-green-200' : 'text-gray-500'}`}>
                                                <span className="mr-2">{['A','B','C','D'][idx]})</span>
                                                {alt}
                                                {idx === q.correta && <i className="fas fa-check ml-2"></i>}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                        <h4 className="text-xs font-bold text-blue-900 uppercase mb-2 flex items-center gap-2">
                                            <i className="fas fa-chalkboard-teacher"></i> Comentário
                                        </h4>
                                        <p className="text-sm text-blue-800 leading-relaxed">
                                            {q.justificativa}
                                        </p>
                                        <div className="mt-3 pt-3 border-t border-blue-100 flex items-center gap-2 text-[10px] font-mono text-blue-600">
                                            <i className="fas fa-gavel"></i> {q.artigo_lei}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default NotebookView;
